/**
 * Standard project runtime — the reusable, CLI-facing `AaacRuntime` that every
 * generated component CLI uses, so that NO caller hand-writes `ctx.runtime`.
 * Named "project" because it is driven by the external `project.yaml` manifest.
 *
 * It:
 *   - discovers/loads the external `project.yaml` (walk up from cwd, or explicit
 *     config path) via loadProjectConfig,
 *   - resolves the effective DSL = project `agents` overlay merged over the
 *     bundled `/$embedded/` component DSL (reusing executeWorkflow/executeTask's
 *     loadDslContext merge — the overlay may `extends: /$embedded/` or be
 *     self-contained),
 *   - selects adapter/model from the project model_mapping (env/flag overrides)
 *     via createModelResolver,
 *   - implements runWorkflow → executeWorkflow and runTask → executeTask,
 *     passing bindingPaths / activeGuardrailPolicy / artifactBinding so machine
 *     guardrails operate on the merged DSL.
 */

import { existsSync } from "node:fs";
import { readFile } from "node:fs/promises";
import { resolve, dirname } from "node:path";
import YAML from "yaml";
import { createPipeline, type OtelEmitter } from "@aaac/observability";
import { loadProjectConfig, type ResolvedConfig } from "../generator/config.js";
import { createModelResolver, type ModelMappingConfig } from "./model-resolver.js";
import { executeWorkflow, executeTask } from "./execute.js";
import { serializeObserverEmit, type ObserverEmitFn } from "./observability-bridge.js";
import type { AaacRuntime, AaacInvocationContext } from "../component-binding.js";

export const PROJECT_CONFIG_FILENAME = "project.yaml";
const DEFAULT_ADAPTER = "claude";

/**
 * Walk up from `startDir` to the filesystem root looking for the nearest
 * `project.yaml`. Returns the absolute path, or undefined if none is found.
 */
export function findProjectConfig(
  startDir: string,
  filename: string = PROJECT_CONFIG_FILENAME,
): string | undefined {
  let dir = resolve(startDir);
  for (;;) {
    const candidate = resolve(dir, filename);
    if (existsSync(candidate)) return candidate;
    const parent = dirname(dir);
    if (parent === dir) return undefined;
    dir = parent;
  }
}

export interface CreateProjectRuntimeOptions {
  /** The bundled `/$embedded/` component DSL (the component's own agent-contracts). */
  embeddedDsl: Record<string, unknown>;
  /** Explicit project.yaml path; when omitted, discovered by walking up from cwd. */
  configPath?: string;
  /** Launch directory for config discovery (defaults to process.cwd()). */
  cwd?: string;
  /** Adapter override (CLI `--adapter`); falls back to env + model_mapping. */
  adapter?: string;
  /** Model override (CLI `--model`). */
  model?: string;
  /** Environment for adapter/model resolution (defaults to process.env). */
  env?: Record<string, string | undefined>;
  /** Auto-approve workflow gates (CLI is non-interactive by default). */
  autoApproveGates?: boolean;
}

export interface ResolvedAdapterSelection {
  adapter: string;
  model?: string;
}

export interface ProjectRuntime {
  /** The standard AaacRuntime — assign to `ctx.runtime` (or use `.context`). */
  runtime: AaacRuntime;
  /** A ready-made invocation context with `runtime` wired in. */
  context: AaacInvocationContext;
  /** The loaded, resolved project config. */
  config: ResolvedConfig;
  /** Absolute path to the discovered/explicit project.yaml. */
  configPath: string;
  /** Absolute path to the project's `agents` overlay DSL (cfg.dslPath). */
  dslSource: string;
  /** Resolve the effective adapter + model (for --dry-run reporting). */
  resolveAdapter(): ResolvedAdapterSelection;
  /**
   * The in-process OtelEmitter created by createPipeline, when observability
   * is configured.  Exposed for test verification that the otel config is
   * actually wired through to the emitter (F-002 / issue #125).
   * Undefined when bindings.observability is absent or disabled.
   */
  otelEmitter?: OtelEmitter;
}

function serializeRequest(input: unknown): string {
  if (typeof input === "string") return input;
  return JSON.stringify(input ?? {});
}

/**
 * Build the standard component runtime from the bundled embedded DSL + the
 * discovered/explicit project.yaml. Throws a clear error when no config exists.
 */
export async function createProjectRuntime(
  options: CreateProjectRuntimeOptions,
): Promise<ProjectRuntime> {
  const cwd = options.cwd ?? process.cwd();
  const configPath = options.configPath
    ? resolve(cwd, options.configPath)
    : findProjectConfig(cwd);

  if (!configPath || !existsSync(configPath)) {
    const where = options.configPath
      ? `config path "${options.configPath}"`
      : `${PROJECT_CONFIG_FILENAME} from ${cwd} upward`;
    throw new Error(
      `No ${PROJECT_CONFIG_FILENAME} found (${where}). Pass --config <path> to a project manifest.`,
    );
  }

  const config = await loadProjectConfig(configPath);
  const env = options.env ?? process.env;

  // Only overlay the project `agents` DSL when it is EXPLICITLY set. When
  // omitted, the bundled `/$embedded/` component DSL is used as-is (no overlay).
  // (RuntimeConfigSchema defaults `dsl` to "./agent-contracts.yaml", so we must
  // inspect the raw manifest to tell "omitted" apart from "explicitly set".)
  const rawManifest = YAML.parse(await readFile(configPath, "utf8")) as
    | Record<string, unknown>
    | undefined;
  const overlayExplicit =
    typeof rawManifest?.agents === "string" || typeof rawManifest?.dsl === "string";
  if (overlayExplicit && !existsSync(config.dslPath)) {
    throw new Error(
      `Project agents DSL not found: ${config.dslPath} (referenced by ${configPath}).`,
    );
  }
  const projectDslPath = overlayExplicit ? config.dslPath : undefined;

  // In-process observability: when project.yaml declares `bindings.observability`
  // we auto-wire an in-process pipeline (config-driven, like the guardrail binding)
  // so agent-axis spans (agent.session / agent.subagent / toolchain.execution /
  // evaluate.*) are recorded to SQLite WITHOUT git/Cursor hooks. Defaults to ON
  // when the binding exists; `bindings.observability.enabled: false` opts out.
  // The DB path defaults to DEFAULT_DB_PATH and is overridable via `db_path`.
  // Build the pipeline ONCE here and flow `observer` through ExecuteOptions.
  let observer: ObserverEmitFn | undefined;
  let observerSink: { close(): void } | undefined;
  let observerClosed = false;
  let observerOtelEmitter: OtelEmitter | undefined;
  if (config.observability && config.observability.enabled !== false) {
    const pipeline = createPipeline({
      ...(config.observability.dbPath ? { dbPath: config.observability.dbPath } : {}),
      // Wire OTel endpoint from project.yaml bindings.observability.otel.
      // Falls back to OTEL_EXPORTER_OTLP_ENDPOINT env var inside OtelEmitter when absent.
      // When neither is set, OtelEmitter is a no-op (fail-open, no regression).
      otel: config.observability.otel,
    });
    observer = serializeObserverEmit(pipeline.collector.emit.bind(pipeline.collector));
    observerSink = pipeline.sink;
    observerOtelEmitter = pipeline.otelEmitter;
  }

  /** Close the observability sink once (idempotent). Safe when no sink was built. */
  function closeObserver(): void {
    if (observerSink && !observerClosed) {
      observerClosed = true;
      observerSink.close();
    }
  }

  function resolveAdapter(): ResolvedAdapterSelection {
    const explicit = options.adapter ?? env.AGENT_RUNTIME_ADAPTER;
    if (explicit) {
      return { adapter: explicit, model: options.model };
    }
    const resolver = createModelResolver({
      modelMapping: config.model_mapping as ModelMappingConfig,
      fallbackAdapter: DEFAULT_ADAPTER,
      fallbackModel: options.model,
      env,
    });
    const resolved = resolver.resolve("standard");
    return { adapter: resolved.adapter, model: options.model ?? resolved.model };
  }

  function sharedExecuteOptions() {
    const { adapter, model } = resolveAdapter();
    const explicitAdapter = options.adapter ?? env.AGENT_RUNTIME_ADAPTER;
    return {
      adapter,
      model,
      pinAdapter: Boolean(explicitAdapter),
      dsl: options.embeddedDsl,
      projectDslPath,
      bindingPaths: config.bindingPaths,
      activeGuardrailPolicy: config.active_guardrail_policy,
      artifactBinding: config.artifactsPath
        ? { source: config.artifactsPath }
        : undefined,
      maxFollowUps: config.recovery.max_follow_ups,
      maxRetries: config.recovery.max_retries,
      modelMapping: config.model_mapping as ModelMappingConfig,
      observer,
      // Toolchain.execution observation: only when BOTH in-process observability
      // is wired AND project.yaml declares `toolchains`. Observation is non-
      // destructive (records spans; never gates the workflow) and a no-op when
      // either is absent (AC4 regression safety). Consumed only by executeWorkflow.
      toolchain:
        observer && config.toolchainsPath
          ? {
              toolchainsPath: config.toolchainsPath,
              artifactsPath: config.artifactsPath,
              cwd,
            }
          : undefined,
    };
  }

  const runtime: AaacRuntime = {
    async runWorkflow(workflowId, _options, input, _ctx) {
      try {
        return await executeWorkflow(workflowId, {
          ...sharedExecuteOptions(),
          request: serializeRequest(input),
          autoApproveGates: options.autoApproveGates ?? true,
        });
      } finally {
        // Flush/close the in-process sink on completion (success AND exception).
        closeObserver();
      }
    },
    async runTask(taskId, _options, input, _ctx) {
      try {
        return await executeTask(taskId, {
          ...sharedExecuteOptions(),
          request: serializeRequest(input),
        });
      } finally {
        closeObserver();
      }
    },
  };

  return {
    runtime,
    context: { runtime },
    config,
    configPath,
    dslSource: projectDslPath ?? "/$embedded/",
    resolveAdapter,
    otelEmitter: observerOtelEmitter,
  };
}
